import { SamplePaymentTreatment } from '@codezo/graphql';

/**
 * How the three payment treatments are worded, in one place.
 *
 * The backend labels the *action*, as it does every action, but the enum values
 * are presentation and have no field of their own. They appear on six surfaces —
 * offer authoring, the offer panel, the greenlight picker, the order-side picker,
 * and both order cards — so they live here rather than being retyped and drifting.
 *
 * The enum names the state; the copy describes what happens to the client's
 * money. So PREPAID reads "Deducted from order" rather than "Prepaid" — the term
 * the quote PDF, the offer change log and the old action label already use.
 * WAIVED keeps its own name, which the PDF also prints, because "waived" IS what
 * happens: someone declined to charge.
 */
export const TREATMENT_LABEL: Record<SamplePaymentTreatment, string> = {
	[SamplePaymentTreatment.Charged]: 'Charged',
	[SamplePaymentTreatment.Prepaid]: 'Deducted from order',
	[SamplePaymentTreatment.Waived]: 'Waived',
};

export const TREATMENT_DESCRIPTION: Record<SamplePaymentTreatment, string> = {
	[SamplePaymentTreatment.Charged]:
		'Client pays for the sample, and it stays a cost on the order.',
	[SamplePaymentTreatment.Prepaid]:
		'Client pays up front, and it comes off the production invoice.',
	[SamplePaymentTreatment.Waived]: 'Client is never charged for the sample.',
};

/** Ordered by what the client ends up paying: full price, nothing, nothing. */
export const TREATMENT_OPTIONS: SamplePaymentTreatment[] = [
	SamplePaymentTreatment.Charged,
	SamplePaymentTreatment.Prepaid,
	SamplePaymentTreatment.Waived,
];

/**
 * Only the two non-default treatments earn a marker on a card. CHARGED is the
 * ordinary case and labelling it would put a badge on almost every sample.
 */
export const treatmentMarker = (treatment: SamplePaymentTreatment): string | null =>
	treatment === SamplePaymentTreatment.Charged ? null : TREATMENT_LABEL[treatment];
